import { assign, fromPromise, setup } from 'xstate';
import { showAxiosErrorAction } from '~/features/actions';
import { NOTIFICATION_STATE } from '~/features/xstateSystem';
import { setNotificationsAction } from './actions';
import { NOTIFICATION_API_ROUTES } from './api';
import { Events } from './events';
import { Context } from './types';

export const notificationState = setup({
  actors: {
    get: fromPromise(NOTIFICATION_API_ROUTES.get),
    patch: fromPromise(NOTIFICATION_API_ROUTES.patch),
    readAll: fromPromise(NOTIFICATION_API_ROUTES.readAll),
  },
  types: {
    context: {} as Context,
    events: {} as Events,
    input: {} as Partial<Context>,
  },
  actions: {
    sendError: showAxiosErrorAction,
    setNotifications: assign(setNotificationsAction),
  },
}).createMachine({
  id: NOTIFICATION_STATE,
  systemId: NOTIFICATION_STATE,
  context: ({ input }) => ({
    loading: false,
    notifications: [],
    isOpen: false,
    userId: input.userId || null,
    userRole: input.userRole || null,
    pendingReadIds: [],
    inFlightReadIds: [],
  }),
  initial: '$_IDLE',
  on: {
    ON_LOAD: {
      target: '.$_GET',
      actions: [assign({ loading: true }), () => console.log('LOADING NOTIFICATIONS')],
    },
    // Targetless on purpose: a transition into $_PATCH from here would exit
    // and re-enter the state, cancelling an in-flight PATCH and losing that
    // dismissal server-side. Instead the id is queued and $_IDLE drains the
    // queue, so rapid dismissals batch into sequential PATCHes.
    ON_READ: {
      // Optimistically drop the notification so the list doesn't refetch and
      // re-render wholesale on every dismiss (MCV-1467).
      actions: assign({
        notifications: ({ context, event }) =>
          context.notifications.filter((n) => n.id !== event.payload.id),
        pendingReadIds: ({ context, event }) =>
          context.pendingReadIds.includes(event.payload.id)
            ? context.pendingReadIds
            : [...context.pendingReadIds, event.payload.id],
      }),
    },
    // [MCV-1918] Dismiss a whole stack of alerts about one listing. Same
    // optimistic remove + queue as ON_READ, for several ids at once.
    ON_READ_MANY: {
      actions: assign({
        notifications: ({ context, event }) =>
          context.notifications.filter((n) => !event.payload.ids.includes(n.id)),
        pendingReadIds: ({ context, event }) =>
          Array.from(new Set([...context.pendingReadIds, ...event.payload.ids])),
      }),
    },
    // [MCV-1918] "Mark all as read". Unlike the queued dismissals this does
    // re-enter a state: the single UPDATE covers every id that was queued or in
    // flight, so cancelling an in-flight PATCH loses nothing.
    ON_READ_ALL: {
      target: '.$_READ_ALL',
    },
    ON_TOGGLE_PANEL: {
      actions: assign({ isOpen: ({ context }) => !context.isOpen }),
    },
  },
  states: {
    $_IDLE: {
      entry: assign({ loading: false }),
      // Drain queued dismissals; the transition moves them to inFlightReadIds
      // before $_PATCH spawns its actor so the PATCH body sees a stable batch.
      always: {
        guard: ({ context }) => context.pendingReadIds.length > 0,
        target: '$_PATCH',
        actions: assign({
          inFlightReadIds: ({ context }) => context.pendingReadIds,
          pendingReadIds: [],
        }),
      },
    },
    $_GET: {
      // If a PATCH was cancelled mid-flight (e.g. ON_LOAD retargets here),
      // its ids are still dirty server-side; requeue them so they retry
      // instead of being stranded. onError clears inFlightReadIds first, so
      // failed ids are not retried.
      entry: assign({
        pendingReadIds: ({ context }) =>
          Array.from(new Set([...context.pendingReadIds, ...context.inFlightReadIds])),
        inFlightReadIds: [],
      }),
      invoke: {
        id: 'getNotifications',
        src: 'get',
        input: (inputArgs) => inputArgs,
        onDone: {
          target: `$_IDLE`,
          actions: ['setNotifications'],
        },
        onError: {
          target: `$_IDLE`,
          actions: ['sendError'],
        },
      },
    },
    $_READ_ALL: {
      // The queued and in-flight dismissal ids are kept until the request
      // settles: if read-all fails, the in-flight PATCH it cancelled and any
      // dismissal queued behind it must still be sent.
      entry: assign({ notifications: [] }),
      invoke: {
        id: 'readAllNotifications',
        src: 'readAll',
        onDone: {
          // Refetch rather than trusting the optimistic empty list: an alert
          // that arrived while the request was out must still show. The single
          // UPDATE covered every id that was queued or in flight, so those are
          // dropped here (before $_GET's entry would requeue them).
          target: `$_GET`,
          actions: assign({ pendingReadIds: [], inFlightReadIds: [] }),
        },
        onError: {
          // Resync so alerts the server did not mark reappear. The ids are
          // untouched: $_GET's entry requeues the cancelled in-flight ids into
          // pendingReadIds, and $_IDLE then sends them through the normal queue.
          target: `$_GET`,
          actions: ['sendError'],
        },
      },
      on: {
        // A reload mid-flight could read the list before the UPDATE lands and
        // resurrect cleared alerts; onDone refetches anyway.
        ON_LOAD: {},
      },
    },
    $_PATCH: {
      invoke: {
        id: 'patchNotification',
        src: 'patch',
        input: (inputArgs) => inputArgs,
        onDone: {
          // $_IDLE's always-transition picks up dismissals queued while this
          // PATCH was in flight.
          target: `$_IDLE`,
          actions: assign({ inFlightReadIds: [] }),
        },
        onError: {
          // Refetch to resync with the server; ids that failed to patch will
          // reappear rather than staying dismissed only locally.
          target: `$_GET`,
          actions: ['sendError', assign({ inFlightReadIds: [] })],
        },
      },
    },
  },
});
